import React, {useEffect, useMemo, useState} from "react";
import classnames from "classnames";
import moment from "moment";
import {toast} from "react-toastify";
import {makeApolloClientCall, makeApolloClientMutationCall} from "../../Api/makeApolloApiCall";
import {fetchAllStoreUniversePages, STORE_UNIVERSE_SUMMARY_PAGE_SIZE} from "./storeUniversePaging";
import {LoadingWrapper} from '../Components/LoadingWrapper';
import ReactTable from '../Components/ReactTable';
import {makeApiCall} from "../../Api/makeApiCall";
import SelectDropdown from "../Components/Dropdowns/SelectDropdown";
import {wrapValueInOptionStructure} from "../../utils/maybeAddLabelToOptions";
import {toastError, toastSuccess} from "../../utils/toast";
import {AVAILABLE_PROJECTION_VERSIONS_QUERY} from "./Components/VersionSelect";
import ConfirmationModal from "../Components/ConfirmationModal";
import {useProjectionLoadTimer} from "./Components/ProjectionLoadTimer";
import {toUniverseName} from "./Components/storeUniverseTableHelpers";

export const COPY_PROJECTION_UNIVERSE_QUERY = `
    mutation($fromVersion: String!, $toVersion: String!, $universe: String!) {
        copyProjectionUniverse(fromVersion: $fromVersion, toVersion: $toVersion, universe: $universe) {
            fromVersion
            toVersion
            universe
            membershipEdges
            cellNodes
            storeCellEdges
            storesCreated
            storesOverwritten
        }
    }
`;

export const PROJECTION_UNIVERSES_QUERY = `
    query($version: String!) {
        projectionUniverses(version: $version)
    }
`;


const isRecommendedSample = ({classification, universe}) => {
    const RECOMMENDED_SAMPLE_UNIVERSES = ['REGIONAL_GROCERY', 'NATURAL_ENHANCED'];
    return classification === 'CHAIN'
        && universe?.some(storeUniverse => RECOMMENDED_SAMPLE_UNIVERSES.includes(storeUniverse));
};

const formatUniverse = (universe) =>
    Array.isArray(universe)
        ? universe.join(', ')
        : universe ?? '';

const toUniverseValues = (universe) =>
    Array.isArray(universe) ? universe : (universe ? [universe] : []);

const toUniverseOption = (universe) => ({
    label: universe.replaceAll('_', ' '),
    value: universe,
});

export {STORE_UNIVERSE_SUMMARY_PAGE_SIZE, STORE_UNIVERSE_MAX_IN_FLIGHT} from "./storeUniversePaging";

const storeUniversePageGraphqlQuery = (version, universeFilter, offset, limit) => ` query {
            storeUniverse(universeFilter: [${universeFilter}], version: "${version}", limit: ${limit}, offset: ${offset}) {
                totalItemsCount
                items {
                  bannerName
                  classification
                  salesHistory(last: 1) {
                        annualizedScv
                        estimatedAcv
                        scv
                        date
                      }
                  universe
                  cells {
                    cellId
                    name
                  }
                }
            }
    } `;

export const loadAllStoreUniverseItems = (version, universeFilter, apolloCall = makeApolloClientCall, onProgress) =>
    fetchAllStoreUniversePages({
        fetchPage: (offset, limit) => apolloCall(storeUniversePageGraphqlQuery(version, universeFilter, offset, limit))
            .then(response => response?.storeUniverse),
        pageSize: STORE_UNIVERSE_SUMMARY_PAGE_SIZE,
        onProgress,
    });

export const copyableUniverseTypes = [
    'NATURAL_ENHANCED',
    'REGIONAL_GROCERY',
    'INFRA',
    'C_AND_S',
    'CIRCANA_PET',
    'SPINS_PET',
];

export const copyTargetVersionOptions = (fromVersion, projectionVersionOptions = []) =>
    projectionVersionOptions.filter(({value}) => value !== fromVersion);

export const toUniverseOptions = (universes = []) => universes.map(toUniverseOption);

const OPERATOR_PREDICATES = {
    '>': (cmp) => cmp > 0,
    '>=': (cmp) => cmp >= 0,
    '<': (cmp) => cmp < 0,
    '<=': (cmp) => cmp <= 0,
    '=': (cmp) => cmp === 0,
};

export const parseNumberOperand = (raw) => {
    const numeric = Number(raw);
    return Number.isFinite(numeric) && numeric >= 0 ? numeric : undefined;
};

const isValidYmd = (value) => moment(value, 'YYYY-MM-DD', true).isValid();

export const parseDateOperand = (raw) => (isValidYmd(raw) ? raw : undefined);

export const parseFilterExpression = (input, parseOperand) => {
    if (!input || !input.trim()) return {status: 'empty'};
    const clauses = input.split(/\band\b/i).map(clause => clause.trim()).filter(Boolean);
    if (clauses.length === 0) return {status: 'empty'};

    const parsed = [];
    for (const clause of clauses) {
        const match = clause.match(/^(>=|<=|>|<|=)?\s*(.+)$/);
        const operand = match ? parseOperand(match[2].trim()) : undefined;
        if (operand === undefined) return {status: 'invalid'};
        parsed.push({op: match[1] || '=', operand});
    }
    return {status: 'ok', clauses: parsed};
};

const matchesAllClauses = (value, clauses, compare) =>
    clauses.every(({op, operand}) => OPERATOR_PREDICATES[op](compare(value, operand)));

const numericCompare = (value, operand) => Math.sign(value - operand);
const lexicalCompare = (value, operand) => (value < operand ? -1 : value > operand ? 1 : 0);

export const numberExpressionFilter = (row, columnId, filterValue) => {
    const parsed = parseFilterExpression(filterValue, parseNumberOperand);
    if (parsed.status === 'empty') return true;
    if (parsed.status === 'invalid') return false;

    const value = row.getValue(columnId);
    if (value === '' || value === null || value === undefined) return false;
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return false;

    return matchesAllClauses(numeric, parsed.clauses, numericCompare);
};

export const dateExpressionFilter = (row, columnId, filterValue) => {
    const parsed = parseFilterExpression(filterValue, parseDateOperand);
    if (parsed.status === 'empty') return true;
    if (parsed.status === 'invalid') return false;

    const value = row.getValue(columnId);
    if (!isValidYmd(value)) return false;

    return matchesAllClauses(value, parsed.clauses, lexicalCompare);
};

export function ExpressionColumnFilter({column, parseOperand, placeholder}) {
    const value = column.getFilterValue() ?? '';
    const isInvalid = parseFilterExpression(value, parseOperand).status === 'invalid';
    return (
        <input type="text"
               className={classnames('expression-filter', {'expression-filter--invalid': isInvalid})}
               placeholder={placeholder}
               title={isInvalid ? 'Examples: "> 30 and < 35", ">= 10"' : undefined}
               data-testid={`${column.id}-filter-input`}
               value={value}
               onChange={e => column.setFilterValue(e.target.value || undefined)} />
    );
}

export const NumberExpressionColumnFilter = ({column}) =>
    <ExpressionColumnFilter column={column} parseOperand={parseNumberOperand} placeholder="e.g. > 30 and <= 35" />;

export const DateExpressionColumnFilter = ({column}) =>
    <ExpressionColumnFilter column={column} parseOperand={parseDateOperand} placeholder="e.g. >= 2024-01-01" />;

const NUMBER_EXPRESSION_COLUMN = {filter: NumberExpressionColumnFilter, filterFn: numberExpressionFilter};

const distinctJoined = (values) => [...new Set(values.filter(value => value !== null && value !== undefined && value !== ''))]
    .sort()
    .join(', ');

const sumOf = (stores, field) => stores.reduce((total, store) => total + (Number(store[field]) || 0), 0);

const universeValuesOf = (store) => store.universeValues?.length
    ? store.universeValues
    : formatUniverse(store.universe).split(', ').filter(Boolean);

export const summarizeCellsByType = (stores) => {
    const cellIdsByType = stores.flatMap(store => store.cells ?? [])
        .reduce((acc, {name, cellId}) => {
            (acc[name] = acc[name] ?? new Set()).add(cellId);
            return acc;
        }, {});

    return Object.keys(cellIdsByType)
        .sort()
        .map(cellType => `${cellType}: ${[...cellIdsByType[cellType]].sort((a, b) => a - b).join(', ')}`)
        .join(' | ');
};

export const summarizeStoresByBanner = (stores = []) => {
    const storesByBanner = stores.reduce((acc, store) => {
        const bannerName = store.bannerName ?? '';
        (acc[bannerName] = acc[bannerName] ?? []).push(store);
        return acc;
    }, {});

    return Object.entries(storesByBanner)
        .map(([bannerName, bannerStores]) => ({
            bannerName,
            storeCount: bannerStores.length,
            universe: distinctJoined(bannerStores.flatMap(universeValuesOf)),
            classification: distinctJoined(bannerStores.map(store => store.classification)),
            cells: summarizeCellsByType(bannerStores),
            sampledCount: bannerStores.filter(store => store.recommendedSample === 'Yes').length,
            annualizedScv: sumOf(bannerStores, 'annualizedScv'),
            estimatedAcv: sumOf(bannerStores, 'estimatedAcv'),
            scv: sumOf(bannerStores, 'scv'),
        }))
        .sort((a, b) => a.bannerName.localeCompare(b.bannerName));
};

const formatTotal = (value) => value.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});

const sumColumnFooter = (accessorKey, format = (total) => total) => ({table}) =>
    <span>{format(table.getFilteredRowModel().rows.reduce((total, row) => total + (row.original[accessorKey] ?? 0), 0))}</span>;

const summaryColumns = [
    {header: 'BANNER', accessorKey: 'bannerName', size: 250, sticky: 'left'},
    {header: 'STORE CLASSIFICATION', accessorKey: 'classification', filterFn: 'includesString'},
    {header: 'STORES', accessorKey: 'storeCount', ...NUMBER_EXPRESSION_COLUMN, footer: sumColumnFooter('storeCount')},
    {header: 'SAMPLED', accessorKey: 'sampledCount', ...NUMBER_EXPRESSION_COLUMN, footer: sumColumnFooter('sampledCount')},
    {header: 'UNIVERSE', accessorKey: 'universe', filterFn: 'includesString'},
    {header: 'CELLS', accessorKey: 'cells', size: 250, filterFn: 'includesString'},
    {
        header: 'ANNUALIZED SCV',
        accessorKey: 'annualizedScv',
        ...NUMBER_EXPRESSION_COLUMN,
        cell: ({getValue}) => formatTotal(getValue()),
        footer: sumColumnFooter('annualizedScv', formatTotal),
    },
    {
        header: 'CALCULATED ACV',
        accessorKey: 'estimatedAcv',
        ...NUMBER_EXPRESSION_COLUMN,
        cell: ({getValue}) => formatTotal(getValue()),
        footer: sumColumnFooter('estimatedAcv', formatTotal),
    },
    {
        header: 'ROLLING SCV',
        accessorKey: 'scv',
        ...NUMBER_EXPRESSION_COLUMN,
        cell: ({getValue}) => formatTotal(getValue()),
        footer: sumColumnFooter('scv', formatTotal),
    },
];

export function ProjectionMaintenanceUniverseTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const [projectionVersions, setProjectionVersions] = useState([]);
    const [projectionVersion, setProjectionVersion] = useState(undefined);
    const [universeOptions, setUniverseOptions] = useState([]);
    const [universeFilter, setUniverseFilter] = useState(undefined);

    const [copyUniverseModalOpen, setCopyUniverseModalOpen] = useState(false);
    const [copyUniverseVersion, setCopyUniverseVersion] = useState('');
    const [copying, setCopying] = useState(false);
    const [submittedFilters, setSubmittedFilters] = useState(null);
    const [loadProgress, setLoadProgress] = useState(null);
    const {startLoad, finishLoad, cancelLoad} = useProjectionLoadTimer();


    useEffect(() => {
        makeApolloClientCall(AVAILABLE_PROJECTION_VERSIONS_QUERY)
            .then((projectionVersionsResponse) => {
                setProjectionVersions(projectionVersionsResponse.availableProjectionVersions.map(version => version.version));
            });
    }, []);

    useEffect(() => {
        if (!projectionVersion) {
            setUniverseOptions([]);
            return;
        }
        makeApolloClientCall(PROJECTION_UNIVERSES_QUERY, false, {version: projectionVersion})
            .then((response) => setUniverseOptions(toUniverseOptions(response.projectionUniverses)));
    }, [projectionVersion]);

    useEffect(() => {
        if (universeFilter && !universeOptions.some(option => option.value === universeFilter)) {
            setUniverseFilter(undefined);
        }
    }, [universeOptions]);

    const projectionVersionOptions = projectionVersions.map(wrapValueInOptionStructure);
    const copyUniverseVersionOptions = copyTargetVersionOptions(projectionVersion, projectionVersions.map(wrapValueInOptionStructure));
    const isCopyableUniverse = copyableUniverseTypes.includes(universeFilter);
    const copyButtonTitle = !projectionVersion || !universeFilter
        ? 'Select a Projection Version and Universe first'
        : isCopyableUniverse
            ? 'Copy the selected universe to another version'
            : `Copying ${universeFilter} is not supported`;

    const projectionUniverseVersionUrl = (version) => {
        const params = new URLSearchParams({version, universe: universeFilter});
        return `${process.env.REACT_APP_EDP_API_BASE_URL}/projectionUniverseVersion/?${params}`;
    };

    const onSubmit = () => {
        setSubmittedFilters({projectionVersion, universeFilter});
    };

    useEffect(() => {
        if (!submittedFilters) return;

        setLoading(true);
        setLoadProgress(null);
        const loadFromStoreUniverse = () => {
            startLoad('Universe tab');
            return loadAllStoreUniverseItems(
                submittedFilters.projectionVersion,
                submittedFilters.universeFilter,
                makeApolloClientCall,
                setLoadProgress)
                .then((items) => {
                    finishLoad(items.length);
                    setData(items.map(item => {
                        const latestSalesHistory = item.salesHistory?.[0];
                        return {
                            ...item,
                            universe: formatUniverse(item.universe),
                            universeValues: toUniverseValues(item.universe),
                            recommendedSample: isRecommendedSample(item) ? 'Yes' : 'No',
                            annualizedScv: latestSalesHistory?.annualizedScv ?? '',
                            estimatedAcv: latestSalesHistory?.estimatedAcv ?? '',
                            scv: latestSalesHistory?.scv ?? '',
                        };
                    }));
                });
        };

        makeApiCall(projectionUniverseVersionUrl(submittedFilters.projectionVersion))
            .then((savedResponse) => {
                const savedRows = savedResponse?.stores;
                if (savedRows?.length) {
                    setData(savedRows);
                    return undefined;
                }
                return loadFromStoreUniverse();
            })
            .finally(() => {
                cancelLoad();
                setLoading(false);
            });
    }, [submittedFilters]);

    const onCopyUniverseConfirmed = () => {
        const toVersion = copyUniverseVersion.trim();

        setCopying(true);
        makeApolloClientMutationCall(COPY_PROJECTION_UNIVERSE_QUERY, {
            fromVersion: projectionVersion,
            toVersion,
            universe: toUniverseName(universeFilter),
        })
            .then((copyResponse) => {
                const {membershipEdges, cellNodes, storeCellEdges, storesCreated, storesOverwritten} = copyResponse.copyProjectionUniverse;
                toastSuccess(toast, `Copied ${universeFilter} from ${projectionVersion} to ${toVersion}: ${membershipEdges} stores, ${cellNodes} cells, ${storeCellEdges} store cell assignments, ${storesCreated} stores added and ${storesOverwritten} updated`);
                setCopyUniverseModalOpen(false);
                setCopyUniverseVersion('');
            })
            .catch(() => toastError(toast, `Failed to copy ${universeFilter} from ${projectionVersion} to ${toVersion}`))
            .finally(() => setCopying(false));
    };

    const bannerSummary = useMemo(() => summarizeStoresByBanner(data ?? []), [data]);

    return (
        <div className={'projection-maintenance-universe-contents '}>
            <div className={'universe-review-filters'} data-testid={'projection_factors_version'}>
                <SelectDropdown id={"projection_version_dropdown"}
                                className={"optional-dropdown"}
                                dataTestId={"projection_version"}
                                inputLabel={"Projection Version"}
                                selectValue={projectionVersion}
                                options={projectionVersionOptions}
                                onChange={(option) => setProjectionVersion(option?.value)}
                />
                <SelectDropdown id={"universe_filter_dropdown"}
                                className={"optional-dropdown"}
                                dataTestId={"universe_filter"}
                                inputLabel={"Universe"}
                                selectValue={universeFilter}
                                options={universeOptions}
                                onChange={(option) => setUniverseFilter(option?.value)}
                />
                <button type={'button'}
                        className={'button primary projection-new-stores-submit-button'}
                        data-testid={'projection-new-stores-submit-button'}
                        disabled={!projectionVersion || !universeFilter}
                        title={projectionVersion && universeFilter ? 'Load stores for the selected filters' : 'Select a Projection Version and Universe first'}
                        onClick={onSubmit}
                >
                    Submit
                </button>

                <button type={'button'}
                        className={'button primary universe-review-copy-button'}
                        data-testid={'copy-universe-button'}
                        disabled={!projectionVersion || !universeFilter || !isCopyableUniverse}
                        title={copyButtonTitle}
                        onClick={() => {
                            setCopyUniverseVersion('');
                            setCopyUniverseModalOpen(true);
                        }}
                >
                    Copy Universe
                </button>
            </div>

            {loading && loadProgress &&
                <div data-testid={'load-from-store-universe-progress'}>
                    Loaded {loadProgress.loadedItemsCount.toLocaleString('en-US')} of {loadProgress.totalItemsCount.toLocaleString('en-US')} stores
                </div>}

            {submittedFilters && <LoadingWrapper loading={loading}>
                <ReactTable
                    className="projection-new-stores-table"
                    filterable
                    hasStickyHeader
                    data={bannerSummary}
                    columns={summaryColumns}
                    initialState={{pagination: {pageSize: 25}}}
                />
            </LoadingWrapper>}

            {copyUniverseModalOpen && <ConfirmationModal headerText={'Copy Universe'}
                                                         confirmButtonText={'Copy'}
                                                         loading={copying}
                                                         submitButtonDisabled={!copyUniverseVersion.trim()}
                                                         confirmationCallback={onCopyUniverseConfirmed}
                                                         cancellationCallback={() => setCopyUniverseModalOpen(false)}
            >
                <div className={'copy-universe-version-field'} data-testid={'copy-universe-version'}>
                    <SelectDropdown id={'copy_universe_version_dropdown'}
                                    inputId={'copy_universe_version_input'}
                                    inputLabel={'Version'}
                                    containerClassName={'copy-projection-version-field'}
                                    dataTestId={'copy-universe-version-dropdown'}
                                    options={copyUniverseVersionOptions}
                                    selectValue={copyUniverseVersion || undefined}
                                    placeholder={'Select a Version...'}
                                    menuPortalTarget={document.body}
                                    styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
                                    onChange={(option) => setCopyUniverseVersion(option?.value ?? '')}
                    />
                </div>
            </ConfirmationModal>}
        </div>
    );
}